import moment from "moment";
import Axios from "./Axios";
import APIVariables, { buildPrescriptionEditPath } from "./api-variables";
import { hasDispensingBrandAssigned } from "../components/common/ViewPAF/utils/viewPafFormLogic";

export async function fetchPrescriptionFieldOptions(productId, patientType) {
  if (!productId || !patientType) return null;
  const res = await Axios.get(
    `${APIVariables.PRESCRIPTION_FIELDS}${productId}/${patientType}`
  );
  return res.data;
}

function firstNumber(value) {
  const match = String(value ?? "").match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

function findOptionIdByValue(options, value) {
  if (!Array.isArray(options) || value == null) return null;
  const str = String(value).trim();
  const exact = options.find(
    (o) => String(o.value).trim() === str || String(o.id) === str
  );
  if (exact) return exact.id;
  const num = firstNumber(str);
  if (num == null) return null;
  const byNum = options.find((o) => firstNumber(o.value) === num);
  return byNum?.id ?? null;
}

function findOtherOptionId(options) {
  if (!Array.isArray(options)) return 0;
  const other = options.find(
    (o) => o.id === 0 || /other/i.test(String(o.value ?? ""))
  );
  return other?.id ?? 0;
}

function resolveNCyclesId(form, fieldOptions) {
  if (form?.new_capsule_cycles?.id != null) {
    return form.new_capsule_cycles.id;
  }
  return (
    findOptionIdByValue(fieldOptions?.n_cycles, form?.n_cycles) ??
    firstNumber(form?.n_cycles)
  );
}

function resolveNWeeksId(form, fieldOptions, displayWeeks) {
  if (displayWeeks && form?.new_capsule_cycles?.id != null) {
    return form.new_capsule_cycles.id;
  }
  if (form?.number_weeks?.id != null) {
    return form.number_weeks.id;
  }
  const fromField =
    findOptionIdByValue(fieldOptions?.n_weeks, form?.n_weeks) ??
    (typeof form?.n_weeks === "number" ? form.n_weeks : firstNumber(form?.n_weeks));
  return fromField ?? 0;
}

function resolveNWeeksOther(form) {
  if (form?.new_capsule_cycles?.id === 0) {
    return form?.n_weeks_other ?? form?.new_capsule_cycles_other ?? null;
  }
  if (form?.n_weeks === 0 || form?.number_weeks?.id === 0) {
    return form?.n_weeks_other ?? null;
  }
  return form?.n_weeks_other ?? null;
}

function resolveWeeksPerCycle(form) {
  if (form?.new_weeks_per_cycle != null) {
    if (form.new_weeks_per_cycle.id === 0) {
      return form.new_weeks_per_cycle_other ?? form.new_weeks_per_cycle.id;
    }
    return form.new_weeks_per_cycle.id ?? form.new_weeks_per_cycle;
  }
  return form?.weeks_per_cycle ?? null;
}

function resolveCapsuleEntry(strength, fieldOptions) {
  const capsuleValue = strength?.new_capsules ?? strength?.capsules;
  const isOther =
    strength?.is_new_other_capsules ||
    capsuleValue === "Other (please state)" ||
    strength?.new_capsules === "Other (please state)";

  if (isOther) {
    return {
      n_capsules: findOtherOptionId(fieldOptions?.capsule_cycle),
      n_capsules_other: strength?.new_capsules_other ?? null,
    };
  }

  const matchId = findOptionIdByValue(fieldOptions?.capsule_cycle, capsuleValue);
  return {
    n_capsules: matchId ?? capsuleValue,
    n_capsules_other: null,
  };
}

/**
 * Build PATCH body for prescriptions/:id/edit/ from in-modal edit state.
 * API uses `strenghts` / `strenght_id` spelling from the backend contract.
 */
export function buildPrescriptionEditPayload(form, fieldOptions) {
  if (!form) return {};

  const displayWeeks = form.schedule_display_type === "weeks";
  const payload = {};

  const nCyclesId = resolveNCyclesId(form, fieldOptions);
  if (nCyclesId != null) payload.n_cycles = nCyclesId;

  const weeksPerCycle = resolveWeeksPerCycle(form);
  if (weeksPerCycle != null) payload.weeks_per_cycle = weeksPerCycle;

  const nWeeksId = resolveNWeeksId(form, fieldOptions, displayWeeks);
  if (nWeeksId != null) payload.n_weeks = nWeeksId;

  const nWeeksOther = resolveNWeeksOther(form);
  if (nWeeksOther != null) payload.n_weeks_other = nWeeksOther;

  if (hasDispensingBrandAssigned(form)) {
    if (form.edit_marketing_holder_id != null) {
      payload.marketing_holder = form.edit_marketing_holder_id;
    }

    const dispensingDate =
      form.edit_dispensing_date ??
      form.amended_dispensed_date ??
      form.dispensed_date;
    if (dispensingDate) {
      const m = moment(dispensingDate);
      if (m.isValid()) {
        payload.dispensing_date = m.toISOString();
      }
    }
  }

  if (Array.isArray(form.strengths) && form.strengths.length > 0) {
    payload.strenghts = form.strengths.map((strength) => {
      const { n_capsules, n_capsules_other } = resolveCapsuleEntry(
        strength,
        fieldOptions
      );
      const entry = {
        strenght_id: strength.strength_id,
        dosage_strength: strength.new_strength_id ?? strength.strength_id,
        n_capsules,
      };
      if (n_capsules_other != null) {
        entry.n_capsules_other = n_capsules_other;
      }
      return entry;
    });
  }

  return payload;
}

export async function patchPrescriptionEdit(prescriptionId, payload) {
  const res = await Axios.patch(
    buildPrescriptionEditPath(prescriptionId),
    payload
  );
  return res.data;
}

export function parsePrescriptionEditError(error) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (data && typeof data === "object") {
    const messages = [];
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        messages.push(`${key}: ${value.join(", ")}`);
      } else if (typeof value === "string") {
        messages.push(`${key}: ${value}`);
      }
    });
    if (messages.length) return messages.join(" ");
  }
  return "An error occurred while saving PAF changes.";
}

/** Whether the Edit action should appear for this prescription. */
export function canShowPrescriptionEditAction({
  canChangePrescription,
  prescription,
  isEditMode,
}) {
  if (!canChangePrescription || !prescription?.id || isEditMode) return false;
  return true;
}
